import React from "react";
import { TGetStaffInboxesForm, TStaffInboxMember } from "../../../../../domain/codecs/form/StaffAwayForm";
import { TTriageForm, TTriageSimpleEmailForm, TTriageBulkEmailMarkAsIrrelevantForm } from "../../../../../domain/codecs/form/TriageForm";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMColors } from "../../../models/CRMColors";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { WeightMedium } from "../../WeightMedium/WeightMedium";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { CRMPill } from "../../Simple/CRMPill/CRMPill";
import { CRMInboxTable } from "../CRMInboxTable/CRMInboxTable";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { Padding } from "../../BuildingBlocks/Padding";
import { CRMThinColoredButton } from "../../Simple/CRMThinColoredButton/CRMThinColoredButton";
import { Background } from "../../BuildingBlocks/Background";

type TCRMStaffInboxesContentProps = {
    staffInboxesForm: TGetStaffInboxesForm;
    staffInboxEmailsForm: TTriageForm;
    selectedUserId: string;
    onSelectUser: (userId: string) => void;
    onToggleAway: (payload: { user_id: string; is_away: boolean }) => void;
    onOpenEmail: (email: TTriageSimpleEmailForm) => void;
    onBulkArchive: (form: TTriageBulkEmailMarkAsIrrelevantForm) => void;
    onLoadMore: () => void;
};

const StaffMemberRow = (props: {
    member: TStaffInboxMember;
    onSelect: () => void;
    onToggleAway: () => void;
}): JSX.Element =>
    <div
        style={{
            padding: `${CRMSpacing.MEDIUM} ${CRMSpacing.MEDIUM}`,
            borderBottom: `1px solid ${CRMColors.NEUTRAL_8}`,
            cursor: "pointer",
        }}
        onClick={props.onSelect}
    >
        <SpacingRow
            spacing={CRMSpacing.MEDIUM}
            alignItems="center"
            justifyContent="space-between"
        >
            {/* LEFT - NAME + AWAY BADGE */}
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                <CRMIcon
                    iconName="person"
                    size="20px"
                    colour="hex"
                    hexColour={props.member.is_away ? CRMColors.NEUTRAL_4 : CRMColors.NEUTRAL_2}
                />
                <FontQuicksand>
                    <FontSize size={CRMFontSizes.SMALL}>
                        <TextColor color={props.member.is_away ? CRMColors.NEUTRAL_4 : CRMColors.NEUTRAL_0}>
                            <WeightSemiBold>
                                {props.member.first_name} {props.member.last_name}
                            </WeightSemiBold>
                        </TextColor>
                    </FontSize>
                </FontQuicksand>
                {props.member.is_away &&
                    <CRMPill colour="highlights-instructional-brick-12">
                        <FontQuicksand>
                            <FontSize size={CRMFontSizes.X_SMALL}>
                                <WeightMedium>away</WeightMedium>
                            </FontSize>
                        </FontQuicksand>
                    </CRMPill>
                }
            </SpacingRow>

            {/* RIGHT - EMAIL COUNT + TOGGLE */}
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                {props.member.unresolved_email_count > 0 &&
                    <CRMPill colour="highlights-calming-pink-6">
                        <FontQuicksand>
                            <FontSize size={CRMFontSizes.X_SMALL}>
                                <WeightSemiBold>
                                    {props.member.unresolved_email_count}
                                </WeightSemiBold>
                            </FontSize>
                        </FontQuicksand>
                    </CRMPill>
                }
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onToggleAway();
                    }}
                    title={props.member.is_away ? "Mark as available" : "Mark as away"}
                >
                    <CRMPill
                        colour={props.member.is_away ? "highlights-positive-green-6" : "highlights-instructional-brick-6"}
                    >
                        <FontQuicksand>
                            <FontSize size={CRMFontSizes.X_SMALL}>
                                <WeightMedium>
                                    {props.member.is_away ? "Set available" : "Set away"}
                                </WeightMedium>
                            </FontSize>
                        </FontQuicksand>
                    </CRMPill>
                </div>
            </SpacingRow>
        </SpacingRow>
    </div>
;

export const CRMStaffInboxesContent = (props: React.PropsWithChildren<TCRMStaffInboxesContentProps>): JSX.Element => {

    const isUserSelected = props.selectedUserId !== "";

    const selectedMember = props.staffInboxesForm.children.staff.find(
        (m) => m.id === props.selectedUserId,
    );

    if (isUserSelected) {
        return (
            <SpacingColumn spacing={CRMSpacing.TINY}>
                {/* BACK BUTTON + USER NAME */}
                <Padding type="custom" spacing={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}>
                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                        <CRMThinColoredButton
                            label="Back"
                            icon="arrow-left"
                            backgroundColor={CRMColors.NEUTRAL_8}
                            onClick={() => props.onSelectUser("")}
                        />
                        {selectedMember &&
                            <FontQuicksand>
                                <FontSize size={CRMFontSizes.SMALL}>
                                    <WeightSemiBold>
                                        <TextColor color={CRMColors.NEUTRAL_0}>
                                            {selectedMember.first_name} {selectedMember.last_name}'s inbox
                                        </TextColor>
                                    </WeightSemiBold>
                                </FontSize>
                            </FontQuicksand>
                        }
                        {selectedMember?.is_away &&
                            <CRMPill colour="highlights-instructional-brick-12">
                                <FontQuicksand>
                                    <FontSize size={CRMFontSizes.X_SMALL}>
                                        <WeightMedium>away</WeightMedium>
                                    </FontSize>
                                </FontQuicksand>
                            </CRMPill>
                        }
                    </SpacingRow>
                </Padding>
                {/* EMAIL TABLE */}
                <CRMInboxTable
                    form={props.staffInboxEmailsForm}
                    onOpenEmail={props.onOpenEmail}
                    onBulkArchive={props.onBulkArchive}
                    onLoadMore={props.onLoadMore}
                />
            </SpacingColumn>
        );
    }

    // STAFF LIST
    return (
        <Background color={CRMColors.NEUTRAL_PAPER}>
            <SpacingColumn spacing="0px">
                {props.staffInboxesForm.children.staff.length === 0 &&
                    <Padding type="custom" spacing={CRMSpacing.X_LARGE}>
                        <FontQuicksand>
                            <FontSize size={CRMFontSizes.SMALL}>
                                <TextColor color={CRMColors.NEUTRAL_4}>
                                    <WeightMedium>No staff members found.</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    </Padding>
                }
                {props.staffInboxesForm.children.staff.map((member) =>
                    <StaffMemberRow
                        key={member.id}
                        member={member}
                        onSelect={() => props.onSelectUser(member.id)}
                        onToggleAway={() => props.onToggleAway({
                            user_id: member.id,
                            is_away: !member.is_away,
                        })}
                    />
                )}
            </SpacingColumn>
        </Background>
    );
};
